/* =============================================================================
 * cart-reco-source.js — Cart cross-sell attribution
 * -----------------------------------------------------------------------------
 * Stamps two hidden line-item properties onto any add-to-cart request that
 * originates from a product carousel rendered on the CART template:
 *
 *   properties[_source]        = "cart_cross_sell"
 *   properties[_source_detail] = "<collection-handle>:<slot position>"
 *
 * Underscore-prefixed properties are hidden from the customer in cart, checkout
 * and order confirmation, but ARE returned by the Admin API / order exports on
 * the line item — which makes cross-sell revenue queryable forever, with no
 * dependency on GA4, GTM or ad-blockers.
 *
 * WHY A NETWORK-LEVEL PATCH
 * The theme has two add paths (halo.actionAddToCart -> Shopify.addItem via
 * jQuery XHR, and halo.actionAddToCart2 via fetch+FormData) and the live add
 * from a cart carousel actually happens inside the GLOBAL quick-view popup,
 * which carries no memory of the carousel that opened it. Patching the two
 * request transports covers every path without editing theme.js (349 KB) or
 * global.min.js (which is served pre-minified and would need rebuilding).
 *
 * Debug: append ?reco_debug=1 to the URL to log decisions to the console.
 * Inspect current state at any time with window.__recoSource()
 *
 * Owner: Analytics | Added: 2026-09-01
 * ============================================================================= */
(function () {
  'use strict';

  var SOURCE_VALUE = 'cart_cross_sell';
  var PROP_SOURCE  = 'properties[_source]';
  var PROP_DETAIL  = 'properties[_source_detail]';
  var STORAGE_KEY  = 'halo_cart_cross_sell_reco';

  /* Carousel roots. The product-block section renders this attribute along with
   * data-collection, so one selector covers "You May Also Like",
   * "Latest Arrivals", or whatever the section is retitled to later. */
  var BLOCK_SELECTOR = '[data-product-block]';

  /* Overlays that CONTINUE an interaction begun in the carousel. A click inside
   * one of these must not clear the recorded source. */
  var CARRY_SELECTOR = [
    '.halo-quick-view-popup',
    '.quick-view-popup',
    '.variants-popup',
    '.quickshop-popup',
    '.addcModal',
    '#halo-card-mobile-popup',
    '.halo-popup',
    '.modal'
  ].join(', ');

  var DEBUG = /[?&]reco_debug=1/.test(window.location.search);

  var pending = null;       // { detail: "cart-product-reco:3" }
  var cartVariants = {};    // variant_id -> true, refreshed from /cart.js

  /* Pre-seed cartVariants from window.__cartVariants if provided by Liquid to save a network request */
  if (window.__cartVariants && Array.isArray(window.__cartVariants)) {
    window.__cartVariants.forEach(function (id) {
      if (id) cartVariants[String(id)] = true;
    });
  }

  /* Grab native transports BEFORE patching, so our own calls never recurse. */
  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  var nativeOpen  = XMLHttpRequest.prototype.open;
  var nativeSend  = XMLHttpRequest.prototype.send;

  function log() {
    if (!DEBUG) return;
    try { console.log.apply(console, ['[reco-source]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  function isCartPage() {
    if (document.body && document.body.classList.contains('template-cart')) return true;
    var path = (window.location && window.location.pathname) || '';
    return /^\/cart(\/|$)/.test(path);
  }

  function isProductPage() {
    if (document.body && document.body.classList.contains('template-product')) return true;
    var path = (window.location && window.location.pathname) || '';
    return /\/products\/[^\/]+/.test(path);
  }

  function savePDPAttribution(detail, handle) {
    if (!handle) return;
    try {
      var payload = {
        detail: detail,
        handle: handle,
        time: Date.now()
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      log('saved PDP attribution to sessionStorage ->', payload);
    } catch (e) {}
  }

  function clearPDPAttribution() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  function injectPDPFormInputs(detail) {
    try {
      var forms = document.querySelectorAll('form[action*="/cart/add"], [data-type="add-to-cart-form"]');
      forms.forEach(function (form) {
        if (!form.querySelector('input[name="' + PROP_SOURCE + '"]')) {
          var inputSource = document.createElement('input');
          inputSource.type = 'hidden';
          inputSource.name = PROP_SOURCE;
          inputSource.value = SOURCE_VALUE;
          form.appendChild(inputSource);
        }
        var existingDetail = form.querySelector('input[name="' + PROP_DETAIL + '"]');
        if (existingDetail) {
          existingDetail.value = detail;
        } else {
          var inputDetail = document.createElement('input');
          inputDetail.type = 'hidden';
          inputDetail.name = PROP_DETAIL;
          inputDetail.value = detail;
          form.appendChild(inputDetail);
        }
      });
      log('injected hidden properties into PDP forms ->', detail);
    } catch (e) {}
  }

  function restorePDPAttribution() {
    if (isCartPage()) return;
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var data = JSON.parse(raw);
      if (!data || !data.detail || !data.handle) return;

      // TTL: 30 minutes
      if (Date.now() - (data.time || 0) > 30 * 60 * 1000) {
        clearPDPAttribution();
        return;
      }

      var pathname = window.location.pathname;
      if (pathname.indexOf('/products/' + data.handle) !== -1 || (data.handle.length > 2 && pathname.indexOf(data.handle) !== -1)) {
        pending = { detail: data.detail, fromPDP: true };
        log('restored PDP attribution ->', data.detail);
        injectPDPFormInputs(data.detail);
      }
    } catch (e) {
      log('restore PDP attribution failed', e);
    }
  }

  function getProductHandle(card) {
    if (!card) return '';
    var jsonStr = card.getAttribute('data-json-product');
    if (jsonStr) {
      try {
        var data = JSON.parse(jsonStr);
        if (data && data.handle) return data.handle;
      } catch (e) {}
    }
    var link = card.querySelector('a[href*="/products/"]');
    if (link) {
      var href = link.getAttribute('href') || '';
      var m = href.match(/\/products\/([a-zA-Z0-9\-_]+)/);
      if (m && m[1]) return m[1];
    }
    return '';
  }

  function isCartAdd(url) {
    return typeof url === 'string' && url.indexOf('/cart/add') !== -1;
  }

  function closest(el, sel) {
    return el && el.closest ? el.closest(sel) : null;
  }

  /* ---------------------------------------------------------------------------
   * 1. Record which carousel slot the shopper touched
   * ------------------------------------------------------------------------ */

  /* Slot position, 1-based. Slider libraries clone slides for infinite loops, so
   * prefer the library's own index attribute and fall back to a de-cloned scan. */
  function slotIndex(block, card) {
    if (!card) return '';

    var swiperSlide = closest(card, '[data-swiper-slide-index]');
    if (swiperSlide) {
      var si = parseInt(swiperSlide.getAttribute('data-swiper-slide-index'), 10);
      if (si >= 0) return String(si + 1);
    }

    var slickSlide = closest(card, '[data-slick-index]');
    if (slickSlide) {
      var ki = parseInt(slickSlide.getAttribute('data-slick-index'), 10);
      if (ki >= 0) return String(ki + 1);
    }

    var cards = [].slice.call(block.querySelectorAll('.product-item')).filter(function (el) {
      return !closest(el, '.slick-cloned') && !closest(el, '.swiper-slide-duplicate');
    });
    var pos = cards.indexOf(card);
    return pos > -1 ? String(pos + 1) : '';
  }

  function listIdFor(block) {
    return block.getAttribute('data-collection') ||
           block.getAttribute('sectionId') ||
           block.id ||
           'unknown';
  }

  /* Capture phase, so this runs before the theme's own delegated handlers. */
  document.addEventListener('click', function (event) {
    if (!isCartPage()) return;

    var target = event.target;
    if (!target || !target.closest) return;

    var block = closest(target, BLOCK_SELECTOR);
    if (block) {
      var card = closest(target, '.product-item');
      var detail = listIdFor(block);
      var pos = slotIndex(block, card);
      if (pos) detail += ':' + pos;
      pending = { detail: detail };
      log('source set ->', detail);

      var handle = getProductHandle(card);
      if (handle) {
        savePDPAttribution(detail, handle);
      }
      return;
    }

    /* Inside a popup opened from the carousel: keep what we recorded. */
    if (closest(target, CARRY_SELECTOR)) return;

    if (pending) {
      log('source cleared (clicked outside carousel)');
      pending = null;
      clearPDPAttribution();
    }
  }, true);

  /* ---------------------------------------------------------------------------
   * 2. Know what is already in the cart
   *
   * Line-item properties make a cart line unique: adding a variant that is
   * already in the cart WITH a property creates a second line instead of
   * incrementing quantity. To avoid a duplicated line, skip stamping when the
   * variant is already present — the first add keeps the attribution anyway.
   * ------------------------------------------------------------------------ */
  function refreshCart() {
    if (!nativeFetch) return;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    nativeFetch(root + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var next = {};
        (cart.items || []).forEach(function (i) { next[String(i.variant_id)] = true; });
        cartVariants = next;
        log('cart variants', Object.keys(cartVariants));
      })
      .catch(function () { /* non-fatal: worst case we stamp and get a 2nd line */ });
  }

  function alreadyInCart(variantId) {
    return !!(variantId && cartVariants[String(variantId)]);
  }

  /* ---------------------------------------------------------------------------
   * 3. Decorate the outgoing /cart/add request
   * ------------------------------------------------------------------------ */

  function encodedPairs() {
    var out = encodeURIComponent(PROP_SOURCE) + '=' + encodeURIComponent(SOURCE_VALUE);
    if (pending && pending.detail) {
      out += '&' + encodeURIComponent(PROP_DETAIL) + '=' + encodeURIComponent(pending.detail);
    }
    return out;
  }

  function alreadyStamped(text) {
    return /properties(\[|%5B)_source(\]|%5D)/i.test(text);
  }

  function decorateString(body) {
    if (alreadyStamped(body)) { log('already stamped, skipping'); return body; }
    var m = /(?:^|&)id=([^&]*)/.exec(body);
    var vid = m ? decodeURIComponent(m[1]) : null;
    if (alreadyInCart(vid)) { log('variant already in cart, not stamping', vid); return body; }
    log('stamping (string)', vid, pending.detail);
    return body + (body.length ? '&' : '') + encodedPairs();
  }

  function decorateFormData(body) {
    if (body.has(PROP_SOURCE)) { log('already stamped, skipping'); return body; }
    var vid = body.get('id');
    if (alreadyInCart(vid)) { log('variant already in cart, not stamping', vid); return body; }
    body.append(PROP_SOURCE, SOURCE_VALUE);
    if (pending.detail) body.append(PROP_DETAIL, pending.detail);
    log('stamping (FormData)', vid, pending.detail);
    return body;
  }

  function decorateBody(body) {
    if (typeof body === 'string') return decorateString(body);
    if (typeof FormData !== 'undefined' && body instanceof FormData) return decorateFormData(body);
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return new URLSearchParams(decorateString(body.toString()));
    }
    log('unrecognised body type, leaving untouched');
    return body;
  }

  /* --- Path A: jQuery $.ajax -> XMLHttpRequest (Shopify.addItem) ----------- */
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__recoUrl = url;
    return nativeOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    var isAdd = isCartAdd(this.__recoUrl);
    if (isAdd && pending) {
      try { body = decorateBody(body); }
      catch (e) { log('xhr decorate failed', e); }
      clearPDPAttribution();
    }
    if (isAdd) {
      this.addEventListener('load', function () {
        setTimeout(function () {
          refreshCart();
          reloadMainCartSection();
        }, 250);
      });
    }
    return nativeSend.call(this, body);
  };

  /* --- Path B: fetch + FormData (halo.actionAddToCart2) -------------------- */
  if (nativeFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url);
      if (isCartAdd(url) && pending && init && init.body) {
        try {
          init = Object.assign({}, init);
          init.body = decorateBody(init.body);
        } catch (e) { log('fetch decorate failed', e); }
        clearPDPAttribution();
      }
      var p = nativeFetch(input, init);
      if (isCartAdd(url)) {
        p.then(function () {
          setTimeout(function () {
            refreshCart();
            reloadMainCartSection();
          }, 250);
        }).catch(function () {});
      }
      return p;
    };
  }

  /* ---------------------------------------------------------------------------
   * Dynamic Cart Re-rendering (Cart template live refresh)
   * When an item is added from the carousel, update the main cart items table
   * and totals dynamically without requiring a page reload.
   * ------------------------------------------------------------------------ */
  function reloadMainCartSection() {
    if (!isCartPage() || !nativeFetch) return;
    var cartEl = document.getElementById('main-cart-items') || document.querySelector('[data-cart]');
    if (!cartEl) return;
    var sectionId = cartEl.getAttribute('data-id') || (cartEl.closest('.shopify-section') && cartEl.closest('.shopify-section').id.replace('shopify-section-', ''));
    if (!sectionId) return;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    nativeFetch(root + 'cart?section_id=' + encodeURIComponent(sectionId), { credentials: 'same-origin' })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');

        var newContent = doc.querySelector('[data-cart-content] .cart');
        var curContent = document.querySelector('[data-cart-content] .cart');
        if (newContent && curContent) {
          curContent.innerHTML = newContent.innerHTML;
        }

        var newTotals = doc.querySelector('[data-cart-total]');
        var curTotals = document.querySelector('[data-cart-total]');
        if (newTotals && curTotals) {
          curTotals.innerHTML = newTotals.innerHTML;
        }

        var newWrapper = doc.querySelector('.cart-content-wrapper');
        var curWrapper = document.querySelector('.cart-content-wrapper');
        if (!curWrapper && newWrapper) {
          var container = document.getElementById('main-cart-items');
          if (container && doc.getElementById('main-cart-items')) {
            container.innerHTML = doc.getElementById('main-cart-items').innerHTML;
          }
        }

        var newCount = doc.querySelector('[data-cart-count]');
        if (newCount) {
          document.querySelectorAll('[data-cart-count]').forEach(function (el) {
            el.textContent = newCount.textContent;
          });
        }
        log('cart section re-rendered dynamically');
      })
      .catch(function (err) {
        log('cart section reload failed', err);
      });
  }

  /* ---------------------------------------------------------------------------
   * 4. Boot
   * ------------------------------------------------------------------------ */
  function boot() {
    refreshCart();
    restorePDPAttribution();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.__recoSource = function () {
    var sessionData = null;
    try { sessionData = JSON.parse(sessionStorage.getItem(STORAGE_KEY)); } catch (e) {}
    return {
      pending: pending,
      cartVariants: Object.keys(cartVariants),
      debug: DEBUG,
      sessionAttribution: sessionData
    };
  };

  log('initialised');
})();
